import { DestroyRef, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { ContentLanguage, FrontendType, StorageKey } from '@coin/shared/core/util-enums';
import { LanguageMapping, UserMenuItem } from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CmsTranslationService, StorageService } from '@coin/shared/core/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class LanguageMenuService {
  private readonly cmsTranslationService = inject(CmsTranslationService);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  private selectableLanguages: LanguageMapping = undefined;

  public selectedLanguage: ContentLanguage;

  constructor() {
    this.initializeSelectedLanguage();
    this.initializeSelectableLanguages();
  }

  public getSelectableLanguageMenuItems(): UserMenuItem[] {
    return Object.entries(this.selectableLanguages)
      .filter(([value]) => !!value && value !== this.selectedLanguage)
      .map(([value]) => {
        return {
          name: `header.user-menu.languages.${value}`,
          action: () => this.selectLanguage(value)
        };
      });
  }

  private selectLanguage(language: string): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          headline: 'general.info',
          translate: true,
          msg: `general.may-need-reload-for-changes`,
          confirmMsg: 'general.btnProceed',
          cancelMsg: 'general.btnCancel'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.translateService.use(language);
        }
      });
  }

  private async initializeSelectableLanguages(): Promise<void> {
    this.selectableLanguages = await firstValueFrom(
      this.cmsTranslationService.getTranslationConfigFile(FrontendType.cosmos)
    );
  }

  private initializeSelectedLanguage(): void {
    this.selectedLanguage = this.storageService.get(StorageKey.LANGUAGE);

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
      this.storageService.set(StorageKey.LANGUAGE, item.lang as ContentLanguage);
      this.selectedLanguage = item.lang as ContentLanguage;
    });
  }
}
